import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Link,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack, CheckCircleOutline } from '@mui/icons-material';
import axiosInstance from '../services/axiosConfig';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import { useAuthFormToast } from '../components/auth/useAuthFormToast';
import {
  AUTH_PRIMARY,
  authFieldSx,
  authMutedLinkSx,
  authPrimaryButtonSx,
} from '../components/auth/authFormStyles';

type Step = 'email' | 'otp' | 'success';

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showError, ToastHost } = useAuthFormToast();
  const [countdown, setCountdown] = useState(0);

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosInstance.post('/api/client-auth/forgot-password', { email });
      setStep('otp');
      startCountdown();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to send OTP. Please try again.';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setLoading(true);

    try {
      await axiosInstance.post('/api/client-auth/forgot-password', { email });
      startCountdown();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to resend OTP. Please try again.';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      showError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      await axiosInstance.post('/api/client-auth/reset-password', {
        email,
        otp_code: otpCode,
        new_password: newPassword,
      });
      setStep('success');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to reset password. Please try again.';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const shellTitle =
    step === 'email'
      ? 'Forgot password'
      : step === 'otp'
        ? 'Enter reset code'
        : 'Password reset successful';

  const shellSubtitle =
    step === 'email'
      ? "Enter your email and we'll send you a code to reset your password."
      : step === 'otp'
        ? `We sent a 6-digit code to ${email}. Enter it below with your new password.`
        : 'Your password has been reset. You can now sign in with your new password.';

  return (
    <>
    <AuthPageShell title={shellTitle} subtitle={shellSubtitle}>
      {step === 'email' && (
        <Box component="form" onSubmit={handleRequestOtp} sx={{ width: '100%' }}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            autoComplete="email"
            autoFocus
            required
            sx={authFieldSx}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !email}
            sx={authPrimaryButtonSx}
          >
            {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Send reset code'}
          </Button>
        </Box>
      )}

      {step === 'otp' && (
        <Box component="form" onSubmit={handleResetPassword} sx={{ width: '100%' }}>
          <TextField
            fullWidth
            label="6-digit code"
            name="otp"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            variant="outlined"
            autoComplete="one-time-code"
            autoFocus
            required
            inputProps={{
              maxLength: 6,
              style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.2rem' },
            }}
            sx={authFieldSx}
          />

          <TextField
            fullWidth
            label="New password"
            name="newPassword"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            variant="outlined"
            autoComplete="new-password"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" aria-label="Toggle password">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={authFieldSx}
          />

          <TextField
            fullWidth
            label="Confirm new password"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            variant="outlined"
            autoComplete="new-password"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    aria-label="Toggle confirm password"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={authFieldSx}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={
              loading || !otpCode || otpCode.length !== 6 || !newPassword || !confirmPassword
            }
            sx={authPrimaryButtonSx}
          >
            {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Reset password'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#565959' }}>
              Didn&apos;t receive the code?{' '}
              {countdown > 0 ? (
                <Typography component="span" sx={{ color: '#9AA0A6' }}>
                  Resend in {countdown}s
                </Typography>
              ) : (
                <Link
                  component="button"
                  type="button"
                  onClick={handleResendOtp}
                  sx={{
                    color: AUTH_PRIMARY,
                    textDecoration: 'none',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Resend
                </Link>
              )}
            </Typography>
          </Box>
        </Box>
      )}

      {step === 'success' && (
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          <CheckCircleOutline sx={{ fontSize: 56, color: '#2E7D32', mb: 2 }} />
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate('/login')}
            sx={authPrimaryButtonSx}
          >
            Go to login
          </Button>
        </Box>
      )}

      {step !== 'success' && (
        <Box textAlign="center" sx={{ width: '100%', mt: 3 }}>
          <Link
            component={RouterLink}
            to="/login"
            sx={{
              ...authMutedLinkSx,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ArrowBack fontSize="small" />
            Back to login
          </Link>
        </Box>
      )}

      <Typography
        variant="caption"
        sx={{ mt: 2.5, color: '#565959', textAlign: 'center', display: 'block' }}
      >
        Having trouble? Contact{' '}
        <Link href="mailto:support@canadawaleyar.com" sx={authMutedLinkSx}>
          support@canadawaleyar.com
        </Link>
      </Typography>
    </AuthPageShell>
    <ToastHost />
    </>
  );
};
